import { useCallback, useEffect, useMemo, useRef } from "react";
import { FilterAction, getFilterDiff, useSegment } from "./useSegment";
import {
  Filter,
  restructureMenu,
  restructureRange,
  restructureRefinements,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_ID,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import { ContextKey } from "src/utils/algolia/contextKeys";
import { useInstantSearch, useRange } from "react-instantsearch-core-v7";
import { IndexUiState } from "instantsearch.js";
import { SearchState } from "react-instantsearch-core";
import { RankingInfo } from "src/contexts/app.context";
import { PRICE_FACET } from "src/utils/algolia";

type UseFilterTrackingProps = {
  listFormat: SEGMENT_LIST_FORMAT;
  listId: SEGMENT_LIST_ID | string;
  listType: SEGMENT_LIST_TYPE | ContextKey;
  shopType?: string;
  indexName: string;
};

type UseFilterTrackingRetrun = {
  trackFilters: (searchState: IndexUiState) => void;
  trackFilterReset: () => void;
};

const sortEventMap: Record<string, { type: string; value: "asc" | "desc" }> = {
  publish_date_desc: { type: "new", value: "desc" },
  discount_date_desc: { type: "new_discount", value: "desc" },
  price_desc: { type: "price", value: "desc" },
  price_asc: { type: "price", value: "asc" },
  popular: { type: "popularity", value: "asc" },
} as const;

function useFilterTracking({
  listFormat,
  listId,
  listType,
  shopType,
  indexName,
}: UseFilterTrackingProps): UseFilterTrackingRetrun {
  const prevListingData = useRef<{
    page: string | number | null | undefined;
    sort: string | null | undefined;
    filters: Filter[];
  }>();
  const { indexUiState, results } = useInstantSearch();
  const { range: initRange } = useRange({ attribute: PRICE_FACET });
  const { productListFiltered, productListFilterReset } = useSegment();

  const { rankingInfo, queryID, sortMapKey } = useMemo(() => {
    const rankingInfo: RankingInfo = {
      abTestID: results.abTestID,
      abTestVariantID: results.abTestVariantID,
      indexUsed: results.index,
    };

    const sortMapKey = results.index.replace(`${indexName}_`, "");

    const queryID = results.queryID;

    return { rankingInfo, indexName, queryID, sortMapKey };
  }, [results, indexName]);

  const trackFilters = useCallback(() => {
    if (
      results.__isArtificial ||
      (initRange.min === 0 && initRange.max === 0)
    ) {
      return;
    }

    const { refinementList, sortBy, page, menu, range } = indexUiState;

    const priceFilter = range?.price?.split(":");

    const relevantFilters = restructureRefinements(refinementList || {});

    const relevantRange = !priceFilter
      ? []
      : restructureRange({
          min: Number(priceFilter[0] ? priceFilter[0] : initRange.min),
          max: Number(priceFilter[1] ? priceFilter[1] : initRange.max),
        });

    const relevantMenu = restructureMenu(menu || {});

    const allFilters = [...relevantFilters, ...relevantRange, ...relevantMenu];

    const filterDiff = getFilterDiff(
      allFilters,
      prevListingData.current?.filters
    ).filter((f) => !!f.value);

    const data: {
      filterAction?: FilterAction;
      filtersApplied?: Filter[];
      sortApplied?: string;
      numPage?: number;
    } = {};

    if (
      page &&
      prevListingData.current?.page !== page &&
      page > (Number(prevListingData.current?.page) ?? 1)
    ) {
      data.filterAction = "next_page";
      data.numPage = page;
    } else if (prevListingData.current?.sort !== sortBy) {
      data.filterAction = "sort";
      data.sortApplied = sortBy;
    } else if (filterDiff.length > 0) {
      data.filterAction = "filter";
      data.filtersApplied = filterDiff;
    }

    if (data.filterAction) {
      void productListFiltered({
        algoliaIndex: indexName,
        meta: {
          listFormat,
          listId,
          listType,
          shopType,
        },
        queryID,
        searchState: indexUiState as SearchState,
        rankingInfo,
        sorts: [sortEventMap[sortMapKey]],
        initPrice: initRange,
        ...data,
      });
    }

    prevListingData.current = {
      page,
      sort: sortBy,
      filters: allFilters,
    };
  }, [
    indexName,
    listFormat,
    listId,
    listType,
    shopType,
    initRange,
    results.__isArtificial,
    queryID,
    indexUiState,
    rankingInfo,
    sortMapKey,
    productListFiltered,
  ]);

  const trackFilterReset = useCallback(
    () =>
      void productListFilterReset({
        algoliaIndex: indexName,
        meta: {
          listFormat,
          listId,
          listType,
          shopType,
        },
        queryID,
        rankingInfo,
      }),
    [
      indexName,
      listFormat,
      listId,
      listType,
      shopType,
      queryID,
      rankingInfo,
      productListFilterReset,
    ]
  );

  useEffect(() => {
    trackFilters();
  }, [trackFilters]);

  return { trackFilters, trackFilterReset };
}

export { useFilterTracking };
